import { useMemo } from 'react'

import {
  formatDate, matchFilterBetweenDates, matchFilterKey, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { TaskListModel } from '@models/taskList'

import type { TaskListModuleState, EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['taskLists']

interface useEntitiesOptions {
  performHttpRequests?: boolean,
  filters: {
    ad_hoc?: boolean,
    end_due_date?: string,
    owner_id?: number,
    owner_type?: string,
    progress_status?: string,
    start_due_date?: string,
    string?: string,
    subject_id?: number,
    subject_type?: string,
    template_checklist?: boolean,
    template_project?: boolean,
  },
  requestOptions?: EntityOptions,
}

type useEntitiesPayload = {
  callbacks: {
    loadMore: () => void,
  },
  canLoadMore: boolean,
  filteredTaskLists: TaskListModel[],
  hasFilteredTaskLists: boolean,
  loading: boolean,
}

function useTaskLists(options: useEntitiesOptions): useEntitiesPayload {
  const { filters, requestOptions } = options || {}
  const {
    ad_hoc: filterAdHoc,
    end_due_date: filterEndDueDate,
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
    progress_status: filterProgressStatus,
    start_due_date: filterStartDueDate,
    string: filterString,
    subject_id: filterSubjectId,
    subject_type: filterSubjectType,
    template_checklist: filterTemplateChecklist,
    template_project: filterTemplateProject,
  } = filters

  const {
    updatedEntities: { taskLists: taskListsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { taskLists } = useSelector(reduxState => reduxState.entities)

  // Filter
  const filteredTaskLists = useMemo((): TaskListModel[] => {
    const filtered = Object.values(taskLists).filter((taskList: TaskListModel) => {
      const {
        ad_hoc = false,
        dates,
        id,
        owner_id,
        owner_type,
        progress_status,
        subject_id,
        subject_type,
        template_checklist,
        template_project,
        title,
      } = taskList

      // @ts-expect-error TS18048
      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const stringMatch = matchId || matchTitle

      const dueAt = formatDate(dates?.due?.date_time, 'dd/MM/yyyy HH:mm')

      const matchAdHoc = typeof filterAdHoc === 'boolean' ? filterAdHoc === (ad_hoc ?? false) : true
      const matchDueDates = matchFilterBetweenDates(dueAt, filterStartDueDate, filterEndDueDate)
      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = matchFilterKey(owner_type, filterOwnerType)
      const matchProgressStatus = matchFilterKey(progress_status, filterProgressStatus)
      const matchSubjectId = matchFilterNumber(subject_id, filterSubjectId)
      const matchSubjectType = matchFilterKey(subject_type, filterSubjectType)
      const matchTemplateChecklist = typeof filterTemplateChecklist === 'boolean'
        ? template_checklist === filterTemplateChecklist
        : true
      const matchTemplateProject = typeof filterTemplateProject === 'boolean'
        ? template_project === filterTemplateProject
        : true

      return (
        matchAdHoc
        && matchDueDates
        && matchOwnerId
        && matchOwnerType
        && matchProgressStatus
        && matchSubjectId
        && matchSubjectType
        && matchTemplateChecklist
        && matchTemplateProject
        && stringMatch
      )
    })

    // Sort by date, then priority
    const sorted = filtered.sort((itemA, itemB) => {
      const dueDateA = itemA?.dates?.due?.integer || 0
      const dueDateB = itemB?.dates?.due?.integer || 0

      // No Due Dates
      if (dueDateA === 0) return 1
      if (dueDateB === 0) return -1

      // Compare Due Dates
      if (dueDateA < dueDateB) return -1
      if (dueDateA > dueDateB) return 1

      return 0
    })

    // @ts-expect-error TS2345
    return sortArrayBy(sorted, 'asc', 'name')
  }, [taskListsUpdatedAt, JSON.stringify(filters)])

  const filteredTaskListsCount: number = filteredTaskLists.length
  const hasFilteredTaskLists: boolean = !!filteredTaskListsCount

  // Load
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTaskListsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  useReduxAction(
    'taskLists',
    'loadTaskLists',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [performHttpRequests],
    {
      shouldPerformFn: () => performHttpRequests,
    },
  )

  const { loading } = useSelector(reduxState => reduxState.taskLists)

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTaskLists,
    hasFilteredTaskLists,
    loading,
  }
}

export default useTaskLists
